import { useEffect, useState } from 'react';
import { useConnectedClient } from 'src/context/ConnectedClientContext';
import { useSwitchTeam } from 'src/hooks/useSwitchTeam';

type UseAutoSwitchTeamParams = {
  targetTeamId: string | null | undefined;
  redirectUrl?: string;
};

/**
 * Hook that automatically switches the user's team if they belong to the target team
 * but are currently logged into a different team.
 *
 * This is useful for shared content (chat, insights, highlights) where users should
 * automatically be switched to the correct team context.
 *
 * @param targetTeamId - The ID of the team that the shared content belongs to
 */
export const useAutoSwitchTeam = ({ targetTeamId, redirectUrl }: UseAutoSwitchTeamParams) => {
  const { details, actions } = useConnectedClient();
  const { switchTeam } = useSwitchTeam({ updateClientDetails: actions.updateClientDetails });
  const [isSwitchingTeam, setIsSwitchingTeam] = useState(false);

  useEffect(() => {
    const currentTeamId = details?.team?.id;
    const userTeams = details?.Teams;

    // Wait for necessary data
    if (!targetTeamId || !currentTeamId || !userTeams || isSwitchingTeam) {
      return;
    }

    // Check if user is already on the correct team
    if (currentTeamId === targetTeamId) {
      return;
    }

    // Check if user belongs to the target team
    const belongsToTargetTeam = userTeams.some(team => team?._id === targetTeamId);

    if (belongsToTargetTeam) {
      setIsSwitchingTeam(true);
      void switchTeam(targetTeamId, redirectUrl);
    }
  }, [targetTeamId, details?.team?.id, details?.Teams, switchTeam, isSwitchingTeam, redirectUrl]);

  return { isSwitchingTeam };
};
